import React, { useState, useEffect } from "react";
import { MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { useTranslation } from "react-i18next";
import {
    IMuiAutocompleteOption,
    MuiAutocomplete,
} from "../framework/muiAutocomplete";
import {
    fetchMyVehicles,
    fetchVehicles,
    setMyVehicles,
} from "../../framework/redux/slices/vehicleSlice";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import {
    handleApiError,
    handleEasyOppApiError,
} from "../../services/baseService";
import { Box, Button, Card, Typography } from "@mui/material";
import { useAsyncThunkAction } from "../../hooks/framework/useAsyncThunkAction";
import { VehicleListItemDto } from "../../models/vehicle/vehicle";
import { LoadingIndicatorInline } from "../framework/loadingIndicator";
import { updateMyVehicles } from "../../services/vehicleService";

export const EmployeeVehicleSelect = () => {
    useAsyncThunkAction(() => fetchVehicles(), { onError: handleApiError }, []);
    useAsyncThunkAction(
        () => fetchMyVehicles(),
        { onError: handleApiError },
        []
    );

    const vehicles = useAppSelector((state) => state.vehicle.idTitles);
    const myVehicles = useAppSelector((state) => state.vehicle.myVehicles);
    const dispatch = useAppDispatch();
    const hasMyVehicles = useAppSelector(
        (state) => state.vehicle.hasMyVehicles
    );
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const convertVehicles = (
        vehicle: VehicleListItemDto
    ): IMuiAutocompleteOption => ({
        id: vehicle.id,
        title: `${vehicle.registerNumber}, ${vehicle.brand}`,
    });

    const [selectedOptions, setSelectedOptions] = useState<
        IMuiAutocompleteOption[]
    >([]);

    useEffect(() => {
        if (hasMyVehicles) {
            setSelectedOptions(myVehicles.map(convertVehicles));
        } else {
            setSelectedOptions([]);
        }
        setIsLoading(false);
    }, [myVehicles, hasMyVehicles]);

    const handleVehicleChange = (
        selectedVehicles: IMuiAutocompleteOption[]
    ) => {
        setSelectedOptions(selectedVehicles);
        const convertSelectedVehicles = selectedVehicles.map((vehicle) => {
            const [registerNumber, brand] = vehicle.title.split(", ");
            return {
                id: vehicle.id,
                registerNumber,
                brand,
            } as VehicleListItemDto;
        });
        const hasMyVehicles = convertSelectedVehicles.length > 0;
        setMyVehicles({ vehicles: convertSelectedVehicles, hasMyVehicles });
    };

    const handleClick = async () => {
        const SaveMyVehicles = {
            VehicleIds: selectedOptions.map((option) => option.id),
        };
        try {
            await updateMyVehicles(SaveMyVehicles, null);
            await dispatch(fetchMyVehicles());
        } catch (e) {
            handleEasyOppApiError(e);
        }
    };

    if (isLoading)
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
            >
                <LoadingIndicatorInline />
            </Box>
        );

    return (
        <MainLayout
            topComponent={
                <PageTitle header={t("settings.vehicles.vehicleSelection")} />
            }
            hasPx
            bottomComponent={
                hasMyVehicles ? (
                    <Card sx={{ bgcolor: "lightBlue.main", padding: 2 }}>
                        <Typography variant="h4" marginBottom={2}>
                            {`${t("settings.vehicles.myVehicles")} (${
                                myVehicles.length
                            } ${t("common.measureUnitPcs")})`}
                        </Typography>
                        {myVehicles.map((vehicle) => (
                            <Typography variant="subtitle2" key={vehicle.id}>
                                {vehicle.registerNumber}, {vehicle.brand}
                            </Typography>
                        ))}
                    </Card>
                ) : (
                    <Typography variant="h4">
                        {t("settings.vehicles.noVehiclesSpecified")}
                    </Typography>
                )
            }
        >
            <MuiAutocomplete
                options={vehicles?.length > 0 ? vehicles : []}
                label={t("common.vehicle")}
                onChange={handleVehicleChange}
                value={selectedOptions}
                helperText={t("settings.vehicles.pickOneOrMore")}
            />
            <Box sx={{ my: 2 }}>
                <Button onClick={() => void handleClick()} variant="contained">
                    {t("settings.vehicles.saveSelections")}
                </Button>
            </Box>
        </MainLayout>
    );
};
